// Canonical shape for the patient's medical list fields — patient-app copy.
//
// `medications`, `allergies`, `preDiagnosedMedicalConditions` and
// `preDiagnosedMentalHealthDisorders` are LISTS of `{name, note}` items. The
// referral-intake and AI-fax creation paths used to write the free-text answer
// as a bare STRING under the same keys, so a tenant holds both types today.
//
// A string has `.length` and `.slice()` but no `.map()`. Every reader that
// guarded with `value.length > 0` walked straight past the guard and died on
// the map — `"N/A".slice(0, 5).map(...)` inside `MedicalHistoryCard` is what
// blanked the whole app behind the root ErrorBoundary, and the same value
// blanked the patient's Medical tab through `UnlimitedItemFields`.
//
// `POST /addPatient` now settles the type on the way in, and the repair script
// fixes the rows written before it did. This module is the READ side of the
// same rule: no stored document, however old, may crash a render again.
//
// Mirrors `blhWebServer/src/utils/patientMedicalLists.js` and
// `blhClinicApp/src/lib/patientMedicalLists.js`. Keep the three in step.

export const PATIENT_MEDICAL_LIST_FIELDS = Object.freeze([
  "medications",
  "allergies",
  "preDiagnosedMedicalConditions",
  "preDiagnosedMentalHealthDisorders",
]);

/**
 * One entry of a medical list, in canonical form, or null when the entry
 * carries nothing.
 *
 * Objects pass through UNTOUCHED: the editor writes `{name: ""}` rows on
 * purpose, long free text lives in `note` with an empty `name`, and older rows
 * carry `{medication}` / `{allergy}` keys.
 */
export function toMedicalListItem(entry) {
  if (entry === null || entry === undefined) return null;
  if (typeof entry === "object" && !Array.isArray(entry)) return entry;
  const text = String(entry).trim();
  return text ? {name: text} : null;
}

/**
 * Coerce any stored value for a medical list field into the canonical array.
 *
 * A free-text answer becomes exactly ONE item carrying that text verbatim.
 * Splitting "Penicillin and Latex" into two allergies would be interpreting
 * clinical data, which no display layer is entitled to do.
 */
export function toMedicalList(value) {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.map(toMedicalListItem).filter(Boolean);
  }
  const item = toMedicalListItem(value);
  return item ? [item] : [];
}

/**
 * Normalize every medical list field on a patient-shaped object.
 *
 * Used on the WRITE side, where a free-text intake answer is turned into the
 * shape the patient document actually stores. Returns a copy; a field that is
 * absent stays absent.
 */
export function normalizePatientMedicalLists(patient) {
  if (!patient || typeof patient !== "object" || Array.isArray(patient)) {
    return patient;
  }

  let next = patient;
  for (const field of PATIENT_MEDICAL_LIST_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(patient, field)) continue;
    const current = patient[field];
    if (Array.isArray(current)) continue;
    if (next === patient) next = {...patient};
    next[field] = toMedicalList(current);
  }
  return next;
}

/**
 * What to show for one item.
 *
 * `note` is a real fallback, not a courtesy: rows written as
 * `{name: "", note: "<long medication list>"}` are already in production and
 * render as "Unknown" without it.
 */
export function medicalListItemLabel(item, fallback = "Unknown") {
  if (item === null || item === undefined) return fallback;
  if (typeof item !== "object") return String(item) || fallback;
  const label =
    item.name ||
    item.medication ||
    item.allergy ||
    item.allergen ||
    item.condition ||
    item.disorder ||
    item.note;
  return (typeof label === "string" && label.trim()) || fallback;
}
